import type { CoinbaseEngUsersAccountManagementApiApiPendingInvite } from '@cbhq/identity-api-hooks/account-management/types';

import type { AvatarColor, UserCellMember } from '../types';

import { canManageEntity } from './canManageMembers';
import type { WorkspaceTeamInvitee, WorkspaceTeamMember } from './workspaceTeam';
import { getMemberDisplayName, toEmailKey } from './workspaceTeam';

type MergePendingInvitesOptions = {
  /** Drops invited roles without a role definition, e.g. useRoles `hasRoleDefinition`. */
  hasRoleDefinition: (role: string) => boolean;
  getAvatarColor: (email: string) => AvatarColor;
  /**
   * When the invite's protected role becomes usable, if any. A member with several
   * invites keeps the earliest.
   */
  getInviteEffectiveAt?: (
    invite: CoinbaseEngUsersAccountManagementApiApiPendingInvite,
  ) => Date | undefined;
};

function getEarlierDate(a: Date | undefined, b: Date | undefined): Date | undefined {
  if (a == null) return b;
  if (b == null) return a;
  return a <= b ? a : b;
}

/**
 * Adds a pending member per invitee to `membersByEmail`, which is keyed by
 * `toEmailKey`. Its name and roles come from the first invite, and `pendingInvites`
 * holds every invite (one per legal entity). Invitees whose email belongs to an
 * active member, in any case, are skipped. Returns a new map and leaves
 * `membersByEmail` untouched.
 */
export function mergePendingInvites(
  membersByEmail: ReadonlyMap<string, UserCellMember>,
  invitees: readonly WorkspaceTeamInvitee[],
  { hasRoleDefinition, getAvatarColor, getInviteEffectiveAt }: MergePendingInvitesOptions,
): Map<string, UserCellMember> {
  const result = new Map(membersByEmail);

  for (const { email, invites } of invitees) {
    const [firstInvite] = invites;
    if (firstInvite && !result.has(toEmailKey(email))) {
      result.set(toEmailKey(email), {
        userUuid: firstInvite.userUuid,
        name: getMemberDisplayName(firstInvite),
        email,
        avatarColor: getAvatarColor(email),
        roles: (firstInvite.roles ?? []).filter(hasRoleDefinition),
        status: 'pending',
        entities: [],
        pendingInvites: invites,
        pendingInviteEffectiveAt: invites
          .map((invite) => getInviteEffectiveAt?.(invite))
          .reduce(getEarlierDate, undefined),
      });
    }
  }

  return result;
}

/**
 * The legal entities of `member`'s pending invites that the current user may
 * manage, deduplicated. Pass `managePermissionsNow` (from useManagePermissionsNow)
 * so an admin role still in its grace period doesn't count.
 */
export function getManageablePendingInviteEntityIds(
  member: Pick<UserCellMember, 'pendingInvites'>,
  workspaceCurrentUser: WorkspaceTeamMember | undefined,
  managePermissionsNow: Date | undefined,
): string[] {
  return [
    ...new Set(
      (member.pendingInvites ?? [])
        .map((invite) => invite.legalEntityId)
        .filter(
          (legalEntityId): legalEntityId is string =>
            legalEntityId != null &&
            canManageEntity(workspaceCurrentUser, legalEntityId, managePermissionsNow),
        ),
    ),
  ];
}
